/**
 * Presence utilities
 * Shared helpers for user online status and last-seen formatting
 */

/** User-chosen availability status (feature #43). */
export type UserStatus = 'online' | 'away' | 'dnd' | 'offline';

/** Bulgarian label per status. */
export const STATUS_LABELS: Record<UserStatus, string> = {
  online: 'Онлайн',
  away: 'Отсъстващ',
  dnd: 'Не безпокойте',
  offline: 'Невидим',
};

/** Tailwind background class for a status dot. */
export const STATUS_DOT_COLORS: Record<UserStatus, string> = {
  online: 'bg-green-500',
  away: 'bg-yellow-500',
  dnd: 'bg-red-500',
  offline: 'bg-gray-400',
};

/**
 * Resolve the status dot to show for another user, combining real-time
 * connection state with their chosen availability status.
 * - Not connected → offline (gray).
 * - Connected → their custom status (away/dnd), or online (green) by default.
 *   A connected user who chose "offline" (invisible) shows gray.
 */
export const effectiveStatus = (
  isOnline: boolean,
  customStatus: UserStatus | undefined
): UserStatus => {
  if (!isOnline) return 'offline';
  return customStatus ?? 'online';
};

/** Tailwind dot color for the effective status of another user. */
export const statusDotColor = (
  isOnline: boolean,
  customStatus: UserStatus | undefined
): string => STATUS_DOT_COLORS[effectiveStatus(isOnline, customStatus)];

/** Format last seen timestamp to human-readable Bulgarian string */
export const formatLastSeen = (lastSeen: string | null | undefined): string => {
  if (!lastSeen) return 'Неизвестно';

  const now = new Date();
  const seen = new Date(lastSeen);
  const diffMs = now.getTime() - seen.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 5) return 'Онлайн';
  if (diffMins < 60) return `Преди ${diffMins} мин`;
  if (diffHours < 24) return `Преди ${diffHours} ч`;
  if (diffDays < 7) return `Преди ${diffDays} дни`;

  return seen.toLocaleDateString('bg-BG', { day: 'numeric', month: 'short' });
};

/** Determine if user is online (seen in last 5 minutes) */
export const isUserOnline = (lastSeen: string | null | undefined): boolean => {
  if (!lastSeen) return false;
  const diffMs = Date.now() - new Date(lastSeen).getTime();
  return diffMs < 5 * 60 * 1000;
};
